import { Clock, ChevronRight, Plus, Target, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { TrainingProgram } from '@/types/training-program';
import { DrillCard } from './DrillCard';
import { InlineEmptyState } from './EmptyStates';

interface TrainingProgramOverviewProps {
  program: TrainingProgram;
  onNavigateToDrill: (drillId: string) => void;
  onNavigateToAddOptional: () => void;
  onNavigateToStart: (drillId: string) => void;
  onNavigateToSwingAnalysis?: () => void;
}

export function TrainingProgramOverview({
  program,
  onNavigateToDrill,
  onNavigateToAddOptional,
  onNavigateToStart,
  onNavigateToSwingAnalysis,
}: TrainingProgramOverviewProps) {
  const optionalSlotsAvailable = program.optional_drills.length < 2;

  return (
    <div className="min-h-screen pb-24 bg-white">
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-white/70">Training Program</p>
              <h1 className="text-2xl font-semibold">{program.program_title}</h1>
              <p className="text-sm text-white/80 mt-1">Primary focus: {program.primary_fault}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 px-3 py-2 rounded-full">
              <Clock className="w-4 h-4" />
              <span>{program.estimated_time_minutes} min</span>
            </div>
          </div>

          {onNavigateToSwingAnalysis && (
            <button
              onClick={onNavigateToSwingAnalysis}
              className="text-sm text-white/90 flex items-center gap-1 hover:text-white transition-colors"
            >
              Back to your swing analysis
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        <section className="space-y-4">
          <div>
            <h2 className="text-xl text-[#0D382A]">Required Drills</h2>
            {/* UX-AUDIT-V3-001: Subheading for required drills section */}
            <p className="text-sm text-gray-600 mt-1">Complete these drills to address your primary swing fault</p>
          </div>
          <div className="space-y-4">
            {program.required_drills.map((drill) => (
              <div key={drill.drill_id} className="space-y-2">
                <DrillCard
                  drill={drill}
                  variant="required"
                  showActions
                  onStart={onNavigateToStart}
                />
                <button
                  onClick={() => onNavigateToDrill(drill.drill_id)}
                  className="text-sm text-[#196644] flex items-center gap-1"
                >
                  View drill details
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-[#0D382A]">Optional Drills</h2>
            {optionalSlotsAvailable && (
              <Button variant="outline" onClick={onNavigateToAddOptional} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Optional Drill
              </Button>
            )}
          </div>

          {program.optional_drills.length > 0 ? (
            <div className="space-y-4">
              {program.optional_drills.map((drill) => (
                <div key={drill.drill_id} className="space-y-2">
                  <DrillCard drill={drill} variant="optional" showActions onStart={onNavigateToStart} />
                  <button
                    onClick={() => onNavigateToDrill(drill.drill_id)}
                    className="text-sm text-[#196644] flex items-center gap-1"
                  >
                    View drill details
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <InlineEmptyState
              title="No Optional Drills Added"
              description="Focus on your required drills, or add optional drills to build your complete game."
              icon={<Target className="w-5 h-5 text-[#196644]" />}
              action={
                optionalSlotsAvailable
                  ? { label: 'Add Optional Drill', onClick: onNavigateToAddOptional }
                  : undefined
              }
            />
          )}
        </section>

        {optionalSlotsAvailable && (
          <Card>
            <CardContent className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="text-sm text-[#0D382A] font-medium">Add optional drills to round out your game</p>
                <p className="text-sm text-gray-500">Choose from putting, short game, or bunker work.</p>
              </div>
              {/* UX-AUDIT-V3-003: Changed from "Browse" to "Add" language */}
              <Button onClick={onNavigateToAddOptional}>Add Optional Drill</Button>
            </CardContent>
          </Card>
        )}

        {/* UX-AUDIT-V3-002: Blue help card at bottom */}
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#0D382A]">How to Use Your Training Program</h3>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  <li>• Complete required drills first — they target your main swing fault</li>
                  <li>• Add optional drills to work on other areas of your game</li>
                  <li>• Practice consistently for best results</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
